import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import MapImageLayer from "@arcgis/core/layers/MapImageLayer.js";
import WMTSLayer from "@arcgis/core/layers/WMTSLayer.js";
import Map from "@arcgis/core/Map.js";
import Point from "@arcgis/core/geometry/Point.js";
import * as identify from "@arcgis/core/rest/identify.js";
import IdentifyParameters from "@arcgis/core/rest/support/IdentifyParameters.js";
import { polygonSymbol, textSymbol } from './symbol';
import SpatialReference from "@arcgis/core/geometry/SpatialReference.js";
import Zoom from "@arcgis/core/widgets/Zoom.js";
import { BaseMapConfig, MapConfig, ServiceConfig } from './types/mapConfig';
import { ID_NUMBER } from '../idNumber';
import Extent from "@arcgis/core/geometry/Extent.js";
import PopupTemplate from "@arcgis/core/PopupTemplate.js";
import actionIcon from '../../icons/view_list.svg';
import { ObjectType } from '../../contexts/types/card';
import * as query from "@arcgis/core/rest/query.js";
import Query from '@arcgis/core/rest/support/Query';

const MAX_ZOOM_SCALE = 5000;
const ZOOM_EXTENT_PERCENTAGE = 25;

const LANDSLIDE_NAME = "O";
const ENDANGERED_AREA_NAME = "TZ";

export function initialize(container: HTMLDivElement, mapConfig: MapConfig, baseMap: BaseMapConfig) {
	const scale = mapConfig.scale ? mapConfig.scale : 20836;
	const sp = mapConfig.center?.sp ? mapConfig.center.sp : 2180;
	const x = mapConfig.center?.x ? mapConfig.center.x : 592269.3933435552;
	const y = mapConfig.center?.y ? mapConfig.center.y : 208818.67777919944;

	var baseMapLayer = getBaseMap(baseMap);
	const map = new Map({
		layers: [baseMapLayer].concat(getLayers(mapConfig)),
	});

	const initSp = getSpatialReference(sp);

	const startExtent = mapConfig.extent ? new Extent({
		xmax: mapConfig.extent?.xmax,
		ymax: mapConfig.extent?.ymax,
		xmin: mapConfig.extent?.xmin,
		ymin: mapConfig.extent?.ymin,
		spatialReference: initSp
	}) : undefined;

	const view = new MapView({
		map: map,
		scale: scale,
		extent: startExtent,
		center: getCenter(x, y, sp),
		ui: {
			components: ['attribution'],
		},
		spatialReference: initSp
	});

	let zoom = new Zoom({
		view: view
	});

	view.ui.add(zoom, {
		position: "bottom-right"
	});

	view.container = container;
	return view;
}

const getLayers = (mapConfig: MapConfig) => {
	return mapConfig.services.map((service) => {
		return createMapImageLayer(service);
	})
}

const createMapImageLayer = (serviceConfig: ServiceConfig) => {
	let params: any = { url: serviceConfig.url };
	if (serviceConfig.sublayers !== undefined) {
		params.sublayers = serviceConfig.sublayers;
	}
	return new MapImageLayer(params);
}

const getBaseMap = (baseMap: BaseMapConfig) => {
	if (baseMap.type === "WMTS") {
		return new WMTSLayer({
			url: baseMap.url,
			title: baseMap.id,
			opacity: 0.5,
			serviceMode: "KVP"
		})
	}
	return new MapImageLayer({
		url: baseMap.url,
		title: baseMap.id,
		opacity: 0.5
	});
}

export const setCursor = (view: MapView, loading: boolean) => {
	view.container.style.cursor = loading ? "progress" : "default";
}

export const closePopup = (view: MapView) => {
	if (view.popup.visible) {
		view.closePopup();
	}
}

export const initIdentify = (view: MapView, services: ServiceConfig[], event: __esri.ViewClickEvent) => {
	return services
		.filter(s => s.identityLayers.length > 0)
		.map(s => { return executeIdentify(view, s, event) })
}

const executeIdentify = async (view: MapView, serviceConfig: ServiceConfig, event: __esri.ViewClickEvent) => {
	if (serviceConfig.byQuery) {
		return executeIdentifyByQuery(view, serviceConfig, event);
	}
	return _executeIdentify(view, serviceConfig, event);
}

const _executeIdentify = async (view: MapView, serviceConfig: ServiceConfig, event: __esri.ViewClickEvent) => {
	let params = new IdentifyParameters();
	params.tolerance = 3;
	params.layerIds = serviceConfig.identityLayers.map(i => i.id);
	params.layerOption = "visible";
	params.width = view.width;
	params.height = view.height;
	params.geometry = event.mapPoint;
	params.mapExtent = view.extent;
	params.returnGeometry = true;

	const response = await identify
		.identify(serviceConfig.url, params);
	const results = response.results;
	return results.map(function (result: any) {
		let feature = result.feature as __esri.Graphic;
		feature.attributes["layerName"] = getDisplayLayerName(result, feature, serviceConfig);
		feature.attributes["layerId"] = result.layerId;
		feature.attributes["idName"] = serviceConfig.idName;
		feature.attributes["_layerKey"] = getLayerKey(result, feature, serviceConfig);
		feature.popupTemplate = getPopupTemplate(feature, serviceConfig);
		return feature;
	});
}

const executeIdentifyByQuery = async (view: MapView, serviceConfig: ServiceConfig, event: __esri.ViewClickEvent) => {
	let promises = serviceConfig.identityLayers
		.map(i => {
			const queryParams = new Query({
				outFields: [ID_NUMBER],
				returnGeometry: true,
				where: "1=1",
				spatialRelationship: "intersects",
				distance: 2 * view.resolution,
				units: "meters",
				geometry: event.mapPoint
			});
			return query.executeQueryJSON(`${serviceConfig.url}/${i.id}`, queryParams);
		});

	return Promise.all(promises).then((response: any) => {
		let allFeatures = [] as __esri.Graphic[];
		serviceConfig.identityLayers
			.forEach((i, idx: number) => {
				let features = response[idx].features;
				if (features.length > 0) {
					features.forEach(function (feature: __esri.Graphic) {
						feature.attributes["layerName"] = i.key;
						feature.attributes["layerId"] = i.id;
						feature.attributes["idName"] = serviceConfig.idName;
						feature.attributes["_layerKey"] = i.key;
						feature.popupTemplate = getPopupTemplate(feature, serviceConfig);

						allFeatures.push(feature);
					});
				}
			});
		return allFeatures;
	})
}

const getPopupTemplate = (feature: __esri.Graphic, config: ServiceConfig) => {
	// let ID = padZeros(feature.attributes[ID_NUMBER]);
	let ID = feature.attributes[ID_NUMBER];
	if (!ID) { ID = "" };
	return new PopupTemplate({
		title: `${config.idLabel} ({layerName}): ${ID}`,
		actions: config.showInTable ? [{
			type: "button",
			title: "Wyszukaj w tabeli",
			id: "select-popup-feature",
			image: actionIcon
		}] : []
	});
}

const getDisplayLayerName = (result: any, feature: __esri.Graphic, config: ServiceConfig) => {
	if (config.idName === "waitingRoom") {
		const rodzajObiektu = feature.attributes["RODZAJ_OBIEKTU"];
		if (rodzajObiektu === ObjectType.KRO || rodzajObiektu === ObjectType.POO) return LANDSLIDE_NAME;
		if (rodzajObiektu === ObjectType.KRTZ || rodzajObiektu === ObjectType.POTZ) return ENDANGERED_AREA_NAME;
	}
	return result.layerName;
}

const getLayerKey = (result: any, feature: __esri.Graphic, config: ServiceConfig) => {
	if (config.idName === "waitingRoom") {
		const rodzajObiektu = feature.attributes["RODZAJ_OBIEKTU"];
		if (rodzajObiektu === ObjectType.KRO || rodzajObiektu === ObjectType.POO) return LANDSLIDE_NAME;
		if (rodzajObiektu === ObjectType.KRTZ || rodzajObiektu === ObjectType.POTZ) return ENDANGERED_AREA_NAME;
		return result.layerName;
	}
	if (config.idName === "dataBase") {
		return getLayerConfigKey(result, config);
	}
	return result.layerName;
}

const getLayerConfigKey = (result: any, config: ServiceConfig) => {
	const configKey = config?.identityLayers?.find(il => il.id === result?.layerId);
	if (configKey !== undefined && configKey !== null) {
		return configKey.key;
	}
	return result?.layerName;
}

export function highlight(view: MapView, feature: Graphic, withText: boolean = true) {
	const graphic = new Graphic({
		geometry: feature.geometry,
		symbol: polygonSymbol()
	});
	view.graphics.add(graphic);

	if (!withText) return;

	// const text = padZeros(feature.attributes[ID_NUMBER]);
	const labelGraphic = new Graphic({
		geometry: feature.geometry.extent.center,
		symbol: textSymbol(feature.attributes[ID_NUMBER].toString())
	});
	view.graphics.add(labelGraphic);
}

const getCenter = (x: number, y: number, sp: number) => {
	return new Point({
		x: x,
		y: y,
		spatialReference: getSpatialReference(sp)
	});
}

const getSpatialReference = (sp: number) => {
	return new SpatialReference({
		wkid: sp
	});
}

export const showFeatures = (view: MapView, features: __esri.Graphic[]) => {
	closePopup(view);
	view.graphics.removeAll();
	const filteredFeatures = features.filter(f => f.geometry !== null);
	if (filteredFeatures.length === 0 || view.extent === null) return;

	filteredFeatures.forEach(f => {
		highlight(view, f);
	});

	const newExtent = calculateZoomExtent(view, filteredFeatures);
	view.goTo(newExtent).catch(function (error) {
		if (error.name !== "AbortError") {
			console.error(error);
		}
	});
}

export const goToAll = (view: MapView) => {
	const graphics = view.graphics.toArray()
		.filter(g => g.geometry.type === 'polygon');
	if (graphics.length === 0 || view.extent === null) return;

	const newExtent = calculateZoomExtent(view, graphics);
	view.goTo(newExtent).catch(function (error) {
		if (error.name !== "AbortError") {
			console.error(error);
		}
	});
}

const calculateZoomExtent = (view: MapView, features: __esri.Graphic[]) => {
	const firstFeatureExtent = features[0].geometry.extent;
	let xmax = firstFeatureExtent.xmax;
	let ymax = firstFeatureExtent.ymax;
	let xmin = firstFeatureExtent.xmin;
	let ymin = firstFeatureExtent.ymin;
	features.forEach((feature: __esri.Graphic) => {
		const extent = feature.geometry.extent;
		if (extent.xmax > xmax) xmax = extent.xmax;
		if (extent.xmin < xmin) xmin = extent.xmin;
		if (extent.ymax > ymax) ymax = extent.ymax;
		if (extent.ymin < ymin) ymin = extent.ymin;
	});

	const averageX = xmin + (xmax - xmin) / 2;
	const averageY = ymin + (ymax - ymin) / 2;
	const deltaToDestination = (((100 / ZOOM_EXTENT_PERCENTAGE) - 1) / 2) * (ymax - ymin);

	let newExtent = new Extent({
		xmax: averageX + 1,
		ymax: ymax + deltaToDestination,
		xmin: averageX - 1,
		ymin: ymin - deltaToDestination,
		spatialReference: getSpatialReference(view.spatialReference.wkid)
	});

	const currentDeltaY = view.extent.ymax - view.extent.ymin;
	const destinationScale = view.scale * (newExtent.ymax - newExtent.ymin) / currentDeltaY;

	if (destinationScale < MAX_ZOOM_SCALE) {
		const destDeltaY = MAX_ZOOM_SCALE * currentDeltaY / view.scale;
		newExtent.ymax = averageY + destDeltaY / 2;
		newExtent.ymin = averageY - destDeltaY / 2;
	}
	return newExtent;
}
